import React from "react";
import { Link } from "react-router-dom";
import { Hospital, ArrowLeft } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { ThemeToggle } from "@/components/ThemeToggle";
import { cn } from "@/lib/utils";

/** Minimalist branded header — hospital brand only as an accent dot + link color. */
export function HospitalHeader({ subtitle, testId, showBack, backTo, backLabel = "Back" }) {
  const { hospital } = useBranding();
  if (!hospital) return null;
  return (
    <header
      className="flex items-start justify-between gap-4 mb-8 animate-fade-in-up"
      data-testid={testId || "hospital-header"}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center border border-border bg-card shrink-0"
        >
          <Hospital className="w-4 h-4 text-foreground" strokeWidth={2} />
          <span
            className="absolute w-2 h-2 rounded-full brand-dot -mt-6 ml-6 ring-2 ring-card"
            aria-hidden
          />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full brand-dot"
              aria-hidden
            />
            {hospital.logo_text || hospital.name}
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground truncate">
            {hospital.name}
          </h1>
          {subtitle && <div className="text-xs sm:text-sm text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {showBack && backTo && (
          <Link
            to={backTo}
            data-testid="header-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full border border-border bg-card hover:bg-secondary text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
          </Link>
        )}
        <ThemeToggle />
      </div>
    </header>
  );
}

export function BrandedNav({ role }) {
  const { hospCode } = useBranding();
  const location = typeof window !== "undefined" ? window.location.pathname : "";
  const links = role === "doctor"
    ? [
        { to: `/${hospCode}/doctor`, label: "Doctor", testId: "nav-doctor" },
        { to: `/${hospCode}/nurse`, label: "Nurse queue", testId: "nav-nurse" },
        { to: `/${hospCode}/admin`, label: "Admin", testId: "nav-admin" },
      ]
    : [];
  if (links.length === 0) return null;
  return (
    <nav className="flex gap-1 mb-6" data-testid="branded-nav">
      {links.map((l) => {
        const active = location === l.to;
        return (
          <Link
            key={l.to}
            to={l.to}
            data-testid={l.testId}
            className={cn(
              "text-xs font-medium px-3 py-1.5 rounded-full transition-all border",
              active
                ? "bg-foreground text-background border-foreground"
                : "bg-card text-muted-foreground border-border hover:text-foreground"
            )}
          >
            {l.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function BrandedShell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
    </div>
  );
}

/** Minimalist stat block: number is the hero, label small. */
export function StatChip({ icon: Icon, label, value, testId, accent = false }) {
  return (
    <div
      data-testid={testId}
      className="surface-card p-5 flex items-start justify-between gap-3 shadow-sm"
    >
      <div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</div>
        <div className="text-3xl font-semibold text-foreground leading-none mt-2">{value}</div>
      </div>
      {Icon && (
        <div className={cn(
          "w-9 h-9 rounded-lg flex items-center justify-center border border-border",
          accent ? "brand-bg-soft" : "bg-secondary"
        )}>
          <Icon className={cn("w-4 h-4", accent ? "brand-text" : "text-muted-foreground")} />
        </div>
      )}
    </div>
  );
}
